/**
 * Terminal Instructions Component
 * Platform-specific guide for opening the terminal
 */

import { getTerminalOpenInstructions, type Platform } from "@utils/platform";
import { ExpandableSection } from "./ExpandableSection";

interface TerminalInstructionsProps {
  platform: Platform;
}

export function TerminalInstructions({ platform }: TerminalInstructionsProps) {
  const instructions = getTerminalOpenInstructions(platform);

  return (
    <ExpandableSection
      title="New to the terminal? Learn how to open it"
      variant="help"
      icon={
        <svg
          className="w-4 h-4 text-blue-600 dark:text-blue-400"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M8 9l3 3-3 3m5 0h3M5 20h14a2 2 0 002-2V6a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
          />
        </svg>
      }
    >
      <div className="space-y-4">
        {/* What is a terminal explanation */}
        <div className="text-sm text-gray-600 dark:text-gray-400">
          <p className="mb-2">
            <strong className="text-gray-900 dark:text-white">
              What is a terminal?
            </strong>{" "}
            Think of it as a text-based control panel for your computer. Instead
            of clicking buttons, you type commands to tell your computer what to
            do.
          </p>
        </div>

        {/* Opening instructions */}
        <div>
          <h4 className="text-sm font-semibold text-gray-900 dark:text-white mb-2">
            How to open {instructions.appName}:
          </h4>
          <ol className="space-y-2">
            {instructions.steps.map((step, index) => (
              <li key={index} className="flex items-start gap-3">
                <span className="flex-shrink-0 w-6 h-6 bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300 rounded-full flex items-center justify-center text-xs font-bold">
                  {index + 1}
                </span>
                <span className="text-sm text-gray-700 dark:text-gray-300 pt-0.5">
                  {step}
                </span>
              </li>
            ))}
          </ol>
        </div>

        {/* Keyboard shortcut highlight */}
        {instructions.shortcut && (
          <div className="flex items-center gap-2 pt-2">
            <span className="text-xs text-gray-500 dark:text-gray-400">
              Quick shortcut:
            </span>
            <kbd className="px-2 py-1 text-xs font-mono bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200 rounded border border-gray-300 dark:border-gray-600">
              {instructions.shortcut}
            </kbd>
          </div>
        )}

        {/* Visual hint */}
        <div className="mt-3 p-3 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
          <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">
            You'll see a window that looks something like this:
          </p>
          <div className="bg-gray-900 rounded p-3 font-mono text-xs text-green-400">
            <div className="flex items-center gap-2 mb-1">
              <div className="flex gap-1">
                <div className="w-2.5 h-2.5 rounded-full bg-red-500" />
                <div className="w-2.5 h-2.5 rounded-full bg-yellow-500" />
                <div className="w-2.5 h-2.5 rounded-full bg-green-500" />
              </div>
              <span className="text-gray-500 text-[10px]">
                {instructions.appName}
              </span>
            </div>
            <div className="text-gray-400">
              {platform === "windows" ? "C:\\Users\\you>" : "~$"}{" "}
              <span className="animate-pulse">|</span>
            </div>
          </div>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
            This is where you'll type the commands shown in this guide.
          </p>
        </div>
      </div>
    </ExpandableSection>
  );
}
